/**
 * useBridge Hook
 * 輕量級 Hook - 只處理 Bridge 連接和通訊層包裝
 * 業務邏輯應該放在各自的 Context 中（StudentContext, GameContext 等）
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { BridgeService } from '@/services/bridge/BridgeService'
import type {
  Student,
  GameInfo,
  StudentPickedEvent,
  StudentRemovedEvent,
  EndGameEvent,
  ConnectionInfo,
} from '@/services/bridge/types/bridge.types'
import type { PlatformType } from '@/services/bridge/types/platform.types'

interface UseBridgeReturn {
  // 狀態
  isLoading: boolean
  error: string | null
  isConnected: boolean
  connectionInfo: ConnectionInfo
  platform: PlatformType

  // Bridge 方法（包裝過，自動處理 loading/error）
  getStudentList: () => Promise<Student[]>
  studentPicked: (event: StudentPickedEvent) => Promise<boolean>
  studentRemoved: (event: StudentRemovedEvent) => Promise<boolean>
  getAccessToken: () => Promise<string>
  getCurrentGameInfo: () => Promise<GameInfo>
  sendEndGame: (event?: EndGameEvent) => Promise<void>

  // 工具方法
  clearError: () => void
  reconnect: () => Promise<void>
}

export function useBridge(): UseBridgeReturn {
  const bridgeRef = useRef<BridgeService | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo>({
    status: 'disconnected',
    platform: 'web',
  })
  const [platform, setPlatform] = useState<PlatformType>('web')

  /**
   * 初始化並連接 Bridge
   */
  const initBridge = useCallback(async () => {
    if (!bridgeRef.current) {
      bridgeRef.current = BridgeService.getInstance()
    }

    // 如果尚未連接，嘗試連接
    if (!bridgeRef.current.isConnected()) {
      try {
        await bridgeRef.current.connect()
        const info = bridgeRef.current.getConnectionInfo()
        setConnectionInfo(info)
        setPlatform(bridgeRef.current.getPlatform())
        setIsConnected(true)
      } catch (err) {
        console.error('Bridge initialization failed:', err)
        setIsConnected(false)
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to initialize bridge'
        setError(errorMessage)
        throw err // 重新拋出錯誤，讓調用方知道連接失敗
      }
    } else {
      // Bridge 已經連接，同步狀態
      const info = bridgeRef.current.getConnectionInfo()
      setConnectionInfo(info)
      setPlatform(bridgeRef.current.getPlatform())
      setIsConnected(true)
    }

    return bridgeRef.current
  }, [])

  /**
   * 包裝異步方法（自動處理 loading/error）
   */
  const wrapAsync = useCallback(
    async <T>(fn: (bridge: BridgeService) => Promise<T>): Promise<T> => {
      setIsLoading(true)
      setError(null)

      try {
        const bridge = await initBridge() // initBridge 會確保連接成功或拋出錯誤
        return await fn(bridge)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error'
        setError(msg)
        console.error('Bridge operation failed:', err)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [initBridge]
  )

  /**
   * 清除錯誤
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * 重新連接
   */
  const reconnect = useCallback(async () => {
    if (bridgeRef.current) {
      bridgeRef.current.disconnect()
      bridgeRef.current = null
    }
    setIsConnected(false)
    await initBridge()
  }, [initBridge])

  /**
   * 自動連接（組件掛載時）
   */
  useEffect(() => {
    initBridge()

    // 組件卸載時斷開連接
    return () => {
      if (bridgeRef.current && bridgeRef.current.isConnected()) {
        bridgeRef.current.disconnect()
      }
    }
  }, [initBridge])

  return {
    // 狀態
    isLoading,
    error,
    isConnected,
    connectionInfo,
    platform,

    // Spinner 方法
    getStudentList: () => wrapAsync(bridge => bridge.getStudentList()),
    studentPicked: (event: StudentPickedEvent) =>
      wrapAsync(bridge => bridge.studentPicked(event)),
    studentRemoved: (event: StudentRemovedEvent) =>
      wrapAsync(bridge => bridge.studentRemoved(event)),

    // Game Leaderboard 方法
    getAccessToken: () => wrapAsync(bridge => bridge.getAccessToken()),
    getCurrentGameInfo: () => wrapAsync(bridge => bridge.getCurrentGameInfo()),
    sendEndGame: (event?: EndGameEvent) =>
      wrapAsync(bridge => bridge.sendEndGame(event)),

    // 工具方法
    clearError,
    reconnect,
  }
}
